/** Окно «Проверка допработ за период» (10.10.2026, владелец): «при клике на заявки доп-работы или
 *  long press — дополнительное окно, где стандартным календарём выбирается период; там все
 *  админ-задачи и все доп-работы, в том числе заявки, без дублирования; если оператор играл —
 *  это тоже видно; за доп во время игры платить нельзя — двойная оплата». Логика — extraReviewModel.ts,
 *  строки — ExtraReviewRows.tsx. Открывается из «Заявок на доп» и «Допработ и админств». */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { api } from "../../api/client";
import { DateSelector, type DateRange } from "../../components/DateSelector";
import { Dropdown, NumPadPop, TimePicker } from "../../components/shared";
import { useExtraReview } from "../../hooks/useExtraReview";
import { addDays, fmtDateApi, fromBusinessDate, parseDateApi, toBusinessDate } from "../../utils/date";
import { buildReview, filterReview, reviewTotals, type RvFilter } from "./extraReviewModel";
import { PersonBlock, type RowActions } from "./ExtraReviewRows";
import css from "./extraReview.module.css";

/** Самый длинный период за раз: зарплатный расчёт всех людей больше чем за 2 месяца — тяжело. */
const MAX_DAYS = 62;
const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const fmt = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const dayLabel = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)} · ${WD[parseDateApi(iso).getUTCDay()]}`;
const FILTERS: RvFilter[] = ["all", "proposals", "warn"];
/** Период не длиннее MAX_DAYS: обрезаем начало, конец оставляем. */
function clamp(from: string, to: string): { from: string; to: string; cut: boolean } {
  const b = parseDateApi(to);
  let a = parseDateApi(from);
  const cut = Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1 > MAX_DAYS;
  if (cut) a = addDays(b, -(MAX_DAYS - 1));
  return { from: fmtDateApi(a), to: fmtDateApi(b), cut };
}

/** onChanged — после любой сохранённой правки (10.10: «Зарплаты» пересчитываются, только если что-то поменялось). */
export function ExtraReview({ from, to, onClose, onChanged }: { from: string; to: string; onClose: () => void; onChanged?: () => void }): ReactElement {
  const [range, setRange] = useState(() => clamp(from, to));
  const [cutNote, setCutNote] = useState(() => range.cut);
  const [filter, setFilter] = useState<RvFilter>("all");
  const [op, setOp] = useState("");
  const { works, sal, loading, error, reload } = useExtraReview(range.from, range.to);
  const days = useMemo(() => buildReview(works, sal, range.from, range.to), [works, sal, range]);
  /* 10.10: выбранного сотрудника нет в новом периоде — не фильтруем по нему (select показывал «все», а список был пуст) */
  const opOk = useMemo(() => (op && days.some((d) => d.people.some((p) => p.operatorId === op)) ? op : ""), [op, days]);
  const shown = useMemo(() => filterReview(days, filter, opOk || null), [days, filter, opOk]);
  const totals = useMemo(() => reviewTotals(shown), [shown]);
  const all = useMemo(() => reviewTotals(filterReview(days, "all", opOk || null)), [days, opOk]);
  const waiting = all.proposals + all.rework + all.factWait;
  const people = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of days) for (const p of d.people) m.set(p.operatorId, p.name);
    return [...m.entries()].sort((x, y) => x[1].localeCompare(y[1], "ru"));
  }, [days]);

  const [pad, setPad] = useState<null | { anchor: HTMLElement; label: string; value: number; save: (v: number) => Promise<void> }>(null);
  const [tp, setTp] = useState<null | { label: string; value: string; save: (f: string, t: string) => Promise<void> }>(null);
  const [err, setErr] = useState("");
  const actions = useMemo<RowActions>(() => ({
    call: async (path, method, body) => {
      setErr("");
      try { await api(path, { method, body, retries: 0 }); onChanged?.(); } catch (e) { setErr(e instanceof Error ? e.message : "Не сохранилось"); }
      reload();
    },
    /* калькулятор сохраняет и при тапе мимо — без изменения значения ничего не пишем (10.10) */
    money: (anchor, label, value, save) => setPad({ anchor, label, value, save: async (v) => { if (v !== value) await save(v); } }),
    time: (label, value, save) => setTp({ label, value, save }),
  }), [reload, onChanged]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !pad && !tp) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [onClose, pad, tp]);

  /* 11.10 (ревью): шапка видна и нажимается — её календарь и меню должны раскрываться ПОВЕРХ окна (AppShell.module.css) */
  useEffect(() => { const r = document.documentElement; r.dataset.rvOpen = "1"; return () => { delete r.dataset.rvOpen; }; }, []);
  /* 11.10 (владелец: «зачем второй календарь?»): календарь периода — в слоте даты шапки, вместо дневного */
  const [hdrSlot, setHdrSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setHdrSlot(document.querySelector<HTMLElement>("[data-hdr-date]")); }, []);
  const [opAnchor, setOpAnchor] = useState<HTMLElement | null>(null); const opBtn = useRef<HTMLButtonElement>(null);
  const [initialRange] = useState<DateRange>(() => ({ start: fromBusinessDate(parseDateApi(range.from)), end: fromBusinessDate(parseDateApi(range.to)) }));
  const onRange = useCallback((v: Date | DateRange) => {
    if (v instanceof Date || !v.start || !v.end) return;
    const next = clamp(fmtDateApi(toBusinessDate(v.start)), fmtDateApi(toBusinessDate(v.end)));
    setCutNote(next.cut);
    setRange(next);
  }, []);

  return createPortal(
    <div className={css.root} role="dialog" aria-modal="true" aria-label="Проверка допработ за период">
      <div className={css.head}>
        {/* календарь периода — в шапке приложения, на месте дневного (один календарь на экране) */}
        <div className={css.titleRow}>
          <span className={css.title}>Проверка допработ</span>
          {hdrSlot ? createPortal(<div data-rv-cal="" className={css.hdrCal}><DateSelector mode="range" initialRange={initialRange} onChange={onRange} publishApi={false} /></div>, hdrSlot)
            : <div className={css.period}><DateSelector mode="range" initialRange={initialRange} onChange={onRange} publishApi={false} /></div>}
          <button type="button" className={css.close} onClick={onClose} aria-label="Закрыть">✕</button>
        </div>
        {cutNote ? <div className={css.hint}>Больше {MAX_DAYS} дней за раз не показываем — взяли последние {MAX_DAYS}.</div> : null}
        <div className={css.chips}>
          {FILTERS.map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)}
              title={f === "proposals" && waiting ? `заявок ${all.proposals}, на доработке ${all.rework}, время по отчёту ${all.factWait} — без «✓» заявок нет в расчётке, время по отчёту идёт по плану` : undefined}
              className={`${css.chip} ${filter === f ? css.chipOn : ""} ${f === "warn" && all.warns ? css.chipWarn : ""} ${f === "proposals" && waiting ? css.chipWait : ""}`}>
              {f === "all" ? "Все" : f === "proposals" ? `Ждут · ${waiting}` : `Конфликты · ${all.warns}`}
            </button>
          ))}
          {/* 11.10 (владелец: «сверхуродливый дропдаун»): вместо системного select — дропдаун приложения */}
          <button ref={opBtn} type="button" className={`${css.chip} ${css.opBtn} ${opOk ? css.chipOn : ""}`} aria-haspopup="listbox"
            onClick={() => setOpAnchor((a) => (a ? null : opBtn.current))}>
            <span className={css.opTxt}>{opOk ? people.find(([id]) => id === opOk)?.[1] ?? "сотрудник" : "все сотрудники"}</span> ▾
          </button>
        </div>
        <div className={css.sum}>
          допработы <b>₽{fmt(totals.paid)}</b> · админства <b>₽{fmt(totals.admin)}</b>
          {totals.proposals ? <> · заявок {totals.proposals} на <b>₽{fmt(totals.proposalSum)}</b></> : null}
          {loading && days.length ? " · обновляю…" : ""}
        </div>
        {/* 11.10 (владелец: «половина экрана занята непонятно чем»): плашка «ждут решения» убрана — счёт на оранжевом чипе
            «Ждут · N» (тап — показать), пояснение — в подсказке чипа и в каждой строке («не войдёт в расчётку, пока не примете») */}
        {err ? <div className={css.err}>{err}</div> : null}
      </div>
      <div className={`${css.body} ${loading && days.length ? css.dim : ""}`}>
        {error ? <div className={css.empty}>Не загрузилось: {error}</div>
          : loading && !days.length ? <div className={css.empty}>загружаю…</div>
          : !shown.length ? <div className={css.empty}>{filter === "all" ? "За этот период допработ и админств нет" : "Ничего не нашлось"}</div>
          : shown.map((d) => (
            <section key={d.date} className={css.day}>
              <div className={css.dayH}>{dayLabel(d.date)}</div>
              {d.people.map((p) => <PersonBlock key={p.operatorId} p={p} date={d.date} a={actions} />)}
            </section>
          ))}
      </div>
      <Dropdown open={!!opAnchor} anchor={opAnchor} multi={false} title="Сотрудник" selectedIds={[opOk]}
        items={[{ id: "", label: "все сотрудники" }, ...people.map(([id, name]) => ({ id, label: name }))]}
        onCommit={(ids) => setOp(String(ids[0] ?? ""))} onClose={() => setOpAnchor(null)} />
      <NumPadPop open={!!pad} anchor={pad?.anchor ?? null} label={pad?.label ?? ""} mode="money" value={pad?.value ?? 0} max={99999}
        onCommit={(v) => { const sv = pad?.save; if (sv) void sv(v); }} onClose={() => setPad(null)}
        extraKeys={[{ label: "Записать", onPress: (v) => { const sv = pad?.save; setPad(null); if (sv) void sv(v); } }]} />
      {tp ? (
        <TimePicker open mode="range" step={5} label={tp.label} value={tp.value || undefined}
          onCommit={(v) => { const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/); const sv = tp.save; setTp(null); if (m) void sv(m[1]!, m[2]!); }}
          onCancel={() => setTp(null)} />
      ) : null}
    </div>,
    document.body,
  );
}
